import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ViewKanban as ViewKanbanIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import VendedorSelect from '../../components/VendedorSelect';
import { ROUTES } from '../../config/routes';
import { useCrmRealtime } from '../../hooks/useCrmRealtime';
import crmCatalogoService from '../../services/crmCatalogoService';
import crmContactoService from '../../services/crmContactoService';
import crmKpiService from '../../services/crmKpiService';
import crmOportunidadService from '../../services/crmOportunidadService';
import crmPipelineService from '../../services/crmPipelineService';
import type {
  CrmContactoListItem,
  CrmKanban,
  CrmKpiDashboard,
  CrmMotivoPerdida,
  CrmOportunidadListItem,
  CrmPipeline,
} from '../../types/crm';
import type { CrmOportunidadRealtimeEvent, CrmRealtimeFuente } from '../../types/crmRealtime';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import CrmDealCard from './components/CrmDealCard';
import CrmKanbanColumn from './components/CrmKanbanColumn';
import CrmPipelineHud from './components/CrmPipelineHud';
import CrmPipelineListView from './components/CrmPipelineListView';
import CrmWinCelebration from './components/CrmWinCelebration';
import CrmAdjuntosPanel from './components/CrmAdjuntosPanel';

const HIGHLIGHT_MS = 8000;

type PipelineSelection = number | 'all' | '';
type PipelineVista = 'kanban' | 'lista';

const normEtapaNombre = (nombre?: string | null) => (nombre ?? '').trim().toLowerCase();

const eventToDeal = (evt: CrmOportunidadRealtimeEvent): CrmOportunidadListItem => ({
  id: evt.oportunidadId,
  nombre: evt.nombre,
  pipelineId: evt.pipelineId,
  etapaId: evt.etapaId,
  nroVendedor: evt.nroVendedor,
  nombreVendedor: evt.nombreVendedor ?? null,
  monto: evt.monto,
  probabilidad: evt.probabilidad ?? null,
  estado: evt.estado,
  contactoPrincipal: evt.contactoPrincipal ?? null,
  diasEnEtapa: 0,
  estancada: false,
  fechaAlta: new Date().toISOString(),
});

const CrmPipelineKanban: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [pipelineId, setPipelineId] = useState<PipelineSelection>('');
  const [vista, setVista] = useState<PipelineVista>('kanban');
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [kanban, setKanban] = useState<CrmKanban | null>(null);
  const [kpi, setKpi] = useState<CrmKpiDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeDeal, setActiveDeal] = useState<CrmOportunidadListItem | null>(null);
  const [drawerDeal, setDrawerDeal] = useState<CrmOportunidadListItem | null>(null);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [nuevaEtapaId, setNuevaEtapaId] = useState<number | null>(null);
  const [nuevaNombre, setNuevaNombre] = useState('');
  const [nuevaMonto, setNuevaMonto] = useState('');
  const [nuevaNroVendedor, setNuevaNroVendedor] = useState<number | null>(null);
  const [nuevaContactos, setNuevaContactos] = useState<CrmContactoListItem[]>([]);
  const [contactosOpts, setContactosOpts] = useState<CrmContactoListItem[]>([]);
  const [loadingContactos, setLoadingContactos] = useState(false);
  const [mostrarAltaContacto, setMostrarAltaContacto] = useState(false);
  const [nuevoContactoNombre, setNuevoContactoNombre] = useState('');
  const [nuevoContactoEmpresa, setNuevoContactoEmpresa] = useState('');
  const [nuevoContactoEmail, setNuevoContactoEmail] = useState('');
  const [nuevoContactoTelefono, setNuevoContactoTelefono] = useState('');
  const [savingContacto, setSavingContacto] = useState(false);
  const [savingNueva, setSavingNueva] = useState(false);
  const [highlightedDealIds, setHighlightedDealIds] = useState<Set<number>>(new Set());
  const [dealFuentes, setDealFuentes] = useState<Map<number, CrmRealtimeFuente | string>>(new Map());
  const [flashEtapaId, setFlashEtapaId] = useState<number | null>(null);
  const [sessionNews, setSessionNews] = useState(0);
  const [sessionWins, setSessionWins] = useState(0);
  const [celebration, setCelebration] = useState<{ nombre: string; monto: number } | null>(null);
  const [perdidaOpen, setPerdidaOpen] = useState(false);
  const [perdidaDeal, setPerdidaDeal] = useState<CrmOportunidadListItem | null>(null);
  const [motivos, setMotivos] = useState<CrmMotivoPerdida[]>([]);
  const [motivoPerdidaId, setMotivoPerdidaId] = useState<number | ''>('');
  const [notaPerdida, setNotaPerdida] = useState('');
  const [closingPerdida, setClosingPerdida] = useState(false);
  const highlightTimers = useRef<Map<number, number>>(new Map());
  const celebratedIds = useRef<Set<number>>(new Set());
  const seenCreateIds = useRef<Set<number>>(new Set());
  const pipelineIdRef = useRef(pipelineId);
  const nroVendedorRef = useRef(nroVendedor);
  const pipelinesRef = useRef(pipelines);
  pipelineIdRef.current = pipelineId;
  nroVendedorRef.current = nroVendedor;
  pipelinesRef.current = pipelines;
  const isAllPipelines = pipelineId === 'all';

  const findEtapaAcrossPipelines = useCallback((etapaId: number) => {
    for (const p of pipelinesRef.current) {
      const e = p.etapas?.find((x) => x.id === etapaId);
      if (e) return e;
    }
    return undefined;
  }, []);

  const resolveEtapaInPipeline = useCallback(
    (dealPipelineId: number, columnEtapa: { nombre: string; esCierreGanado?: boolean; esCierrePerdido?: boolean } | undefined) => {
      if (!columnEtapa) return undefined;
      const pipe = pipelinesRef.current.find((p) => p.id === dealPipelineId);
      const etapas = pipe?.etapas ?? [];
      const byName = etapas.find((e) => normEtapaNombre(e.nombre) === normEtapaNombre(columnEtapa.nombre));
      if (byName) return byName;
      if (columnEtapa.esCierreGanado) return etapas.find((e) => e.esCierreGanado);
      if (columnEtapa.esCierrePerdido) return etapas.find((e) => e.esCierrePerdido);
      return undefined;
    },
    [],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const markHighlighted = useCallback((dealId: number, fuente?: string | null) => {
    setHighlightedDealIds((prev) => new Set(prev).add(dealId));
    if (fuente) {
      setDealFuentes((prev) => {
        const next = new Map(prev);
        next.set(dealId, fuente);
        return next;
      });
    }
    const existing = highlightTimers.current.get(dealId);
    if (existing) window.clearTimeout(existing);
    const t = window.setTimeout(() => {
      setHighlightedDealIds((prev) => {
        const next = new Set(prev);
        next.delete(dealId);
        return next;
      });
      highlightTimers.current.delete(dealId);
    }, HIGHLIGHT_MS);
    highlightTimers.current.set(dealId, t);
  }, []);

  const insertOrMoveDeal = useCallback((deal: CrmOportunidadListItem, etapaId: number) => {
    setKanban((prev) => {
      if (!prev) return prev;
      const nextCols = prev.columnas.map((col) => ({
        ...col,
        oportunidades: col.oportunidades.filter((o) => o.id !== deal.id),
        montoTotal: 0,
      }));
      const meta = findEtapaAcrossPipelines(etapaId);
      const nombreRef = meta?.nombre ?? deal.etapaNombre;
      const target = nextCols.find(
        (c) =>
          c.etapa.id === etapaId
          || (nombreRef != null && normEtapaNombre(c.etapa.nombre) === normEtapaNombre(nombreRef)),
      );
      if (target) {
        const updated = {
          ...deal,
          etapaId,
          etapaNombre: meta?.nombre ?? deal.etapaNombre,
          diasEnEtapa: 0,
        };
        target.oportunidades = [updated, ...target.oportunidades];
      }
      nextCols.forEach((col) => {
        col.montoTotal = col.oportunidades.reduce((s, o) => s + (o.monto || 0), 0);
      });
      return { ...prev, columnas: nextCols };
    });
  }, [findEtapaAcrossPipelines]);

  const flashColumn = useCallback((etapaId: number) => {
    setFlashEtapaId(etapaId);
    window.setTimeout(() => setFlashEtapaId((cur) => (cur === etapaId ? null : cur)), 600);
  }, []);

  const handleRealtimeEvent = useCallback(
    (evt: CrmOportunidadRealtimeEvent) => {
      const selected = pipelineIdRef.current;
      if (selected === '') return;
      if (selected !== 'all' && evt.pipelineId !== selected) return;
      if (nroVendedorRef.current != null && evt.nroVendedor !== nroVendedorRef.current) return;

      const deal = eventToDeal(evt);

      if (evt.tipo === 'OportunidadCreada') {
        if (seenCreateIds.current.has(evt.oportunidadId)) return;
        seenCreateIds.current.add(evt.oportunidadId);
        window.setTimeout(() => seenCreateIds.current.delete(evt.oportunidadId), 15000);
        insertOrMoveDeal(deal, evt.etapaId);
        markHighlighted(evt.oportunidadId, evt.fuente);
        setSessionNews((n) => n + 1);
        flashColumn(evt.etapaId);
        const who = evt.fuente === 'agente' ? 'Un agente creó' : 'Nueva oportunidad';
        enqueueSnackbar(`${who} ${evt.nombre} · ${formatCurrency(evt.monto)}`, {
          variant: evt.fuente === 'agente' ? 'info' : 'success',
        });
        return;
      }

      if (evt.tipo === 'OportunidadEtapaCambiada') {
        insertOrMoveDeal(deal, evt.etapaId);
        flashColumn(evt.etapaId);
        return;
      }

      if (evt.tipo === 'OportunidadCerradaGanada') {
        if (!celebratedIds.current.has(evt.oportunidadId)) {
          celebratedIds.current.add(evt.oportunidadId);
          setCelebration({ nombre: evt.nombre, monto: evt.monto });
          setSessionWins((n) => n + 1);
          window.setTimeout(() => celebratedIds.current.delete(evt.oportunidadId), 10000);
        }
        insertOrMoveDeal({ ...deal, estado: 'Ganada', probabilidad: 100 }, evt.etapaId);
        flashColumn(evt.etapaId);
        return;
      }

      if (evt.tipo === 'OportunidadCerradaPerdida') {
        insertOrMoveDeal({ ...deal, estado: 'Perdida', probabilidad: 0 }, evt.etapaId);
        flashColumn(evt.etapaId);
        enqueueSnackbar(`${evt.nombre} marcada como perdida`, { variant: 'warning' });
      }
    },
    [enqueueSnackbar, flashColumn, insertOrMoveDeal, markHighlighted],
  );

  const { connectionState } = useCrmRealtime(handleRealtimeEvent, true);

  const loadPipelines = useCallback(async () => {
    try {
      const data = await crmPipelineService.getAll();
      const activos = (data ?? []).filter((p) => p.activo);
      setPipelines(activos);
      if (activos.length && pipelineId === '') {
        setPipelineId(activos[0].id);
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar pipelines'), { variant: 'error' });
    }
  }, [enqueueSnackbar, pipelineId]);

  const loadKanban = useCallback(async () => {
    if (pipelineId === '') return;
    try {
      setLoading(true);
      const data = await crmOportunidadService.getKanban({
        ...(pipelineId === 'all' ? {} : { pipelineId }),
        nroVendedor: nroVendedor ?? undefined,
        searchTerm: searchTerm.trim() || undefined,
      });
      setKanban(data);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el kanban'), { variant: 'error' });
      setKanban(null);
    } finally {
      setLoading(false);
    }
  }, [pipelineId, nroVendedor, searchTerm, enqueueSnackbar]);

  const loadKpi = useCallback(async () => {
    if (pipelineId === '') return;
    try {
      const dashboard = await crmKpiService.getDashboard({
        ...(pipelineId === 'all' ? {} : { pipelineId }),
        nroVendedor: nroVendedor ?? undefined,
      });
      setKpi(dashboard);
    } catch {
      setKpi(null);
    }
  }, [pipelineId, nroVendedor]);

  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  useEffect(() => {
    if (vista !== 'kanban') return;
    const t = setTimeout(() => {
      void loadKanban();
      void loadKpi();
    }, 250);
    return () => clearTimeout(t);
  }, [loadKanban, loadKpi, vista]);

  useEffect(() => {
    if (vista !== 'lista') return;
    void loadKpi();
  }, [vista, loadKpi]);

  useEffect(() => {
    return () => {
      highlightTimers.current.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const columnas = useMemo(() => kanban?.columnas ?? [], [kanban]);

  const findDeal = (dealId: number): CrmOportunidadListItem | undefined => {
    for (const col of columnas) {
      const found = col.oportunidades.find((o) => o.id === dealId);
      if (found) return found;
    }
    return undefined;
  };

  const findEtapa = (etapaId: number) =>
    columnas.find((c) => c.etapa.id === etapaId)?.etapa
    ?? findEtapaAcrossPipelines(etapaId)
    ?? (typeof pipelineId === 'number'
      ? pipelines.find((p) => p.id === pipelineId)?.etapas?.find((e) => e.id === etapaId)
      : undefined);

  const columnMatchesEtapa = (colEtapaId: number, etapaId: number) => {
    if (colEtapaId === etapaId) return true;
    const colMeta = findEtapa(colEtapaId);
    const meta = findEtapaAcrossPipelines(etapaId);
    return (
      colMeta != null
      && meta != null
      && normEtapaNombre(colMeta.nombre) === normEtapaNombre(meta.nombre)
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    const deal = event.active.data.current?.deal as CrmOportunidadListItem | undefined;
    setActiveDeal(deal ?? null);
  };

  const openPerdidaDialog = async (deal: CrmOportunidadListItem, etapaCierreId?: number) => {
    setPerdidaDeal({
      ...deal,
      etapaId: etapaCierreId ?? deal.etapaId,
    });
    setMotivoPerdidaId('');
    setNotaPerdida('');
    setPerdidaOpen(true);
    try {
      const list = await crmCatalogoService.getMotivosPerdida();
      setMotivos((list ?? []).filter((m) => m.activo));
    } catch {
      setMotivos([]);
    }
  };

  const celebrateWin = useCallback(
    (nombre: string, monto: number, deal: CrmOportunidadListItem, etapaId: number) => {
      if (celebratedIds.current.has(deal.id)) {
        insertOrMoveDeal({ ...deal, etapaId, estado: 'Ganada', probabilidad: 100 }, etapaId);
        return;
      }
      celebratedIds.current.add(deal.id);
      setCelebration({ nombre, monto });
      setSessionWins((n) => n + 1);
      insertOrMoveDeal({ ...deal, etapaId, estado: 'Ganada', probabilidad: 100 }, etapaId);
      flashColumn(etapaId);
      window.setTimeout(() => {
        void loadKpi();
      }, 400);
      window.setTimeout(() => celebratedIds.current.delete(deal.id), 10000);
    },
    [flashColumn, insertOrMoveDeal, loadKpi],
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDeal(null);
    const { active, over } = event;
    if (!over) return;

    const deal = active.data.current?.deal as CrmOportunidadListItem | undefined;
    if (!deal) return;
    if ((deal.estado || '').toLowerCase() !== 'abierta') {
      enqueueSnackbar('Las oportunidades cerradas no se pueden mover', { variant: 'info' });
      return;
    }

    let columnEtapaId: number | null = null;
    const overId = String(over.id);
    if (overId.startsWith('etapa-')) {
      columnEtapaId = Number(overId.replace('etapa-', ''));
    } else if (over.data.current?.etapaId) {
      columnEtapaId = Number(over.data.current.etapaId);
    } else if (overId.startsWith('deal-')) {
      const overDealId = Number(overId.replace('deal-', ''));
      const overDeal = findDeal(overDealId);
      if (overDeal) columnEtapaId = overDeal.etapaId;
    }

    if (!columnEtapaId) return;

    let targetEtapaId = columnEtapaId;
    const columnEtapa = findEtapa(columnEtapaId);
    if (isAllPipelines) {
      const resolved = resolveEtapaInPipeline(deal.pipelineId, columnEtapa);
      if (!resolved) {
        enqueueSnackbar(
          'Esa etapa no existe en el pipeline de esta oportunidad',
          { variant: 'warning' },
        );
        return;
      }
      targetEtapaId = resolved.id;
    }

    if (targetEtapaId === deal.etapaId) return;

    const etapa = isAllPipelines
      ? findEtapaAcrossPipelines(targetEtapaId) ?? columnEtapa
      : findEtapa(targetEtapaId);
    if (etapa?.esCierreGanado) {
      try {
        await crmOportunidadService.cerrarGanada(deal.id, { etapaId: targetEtapaId });
        celebrateWin(deal.nombre, deal.monto, deal, targetEtapaId);
        enqueueSnackbar('Oportunidad marcada como ganada', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cerrar como ganada'), { variant: 'error' });
        await loadKanban();
      }
      return;
    }

    if (etapa?.esCierrePerdido) {
      await openPerdidaDialog(deal, targetEtapaId);
      return;
    }

    insertOrMoveDeal(
      {
        ...deal,
        etapaId: targetEtapaId,
        etapaNombre: etapa?.nombre ?? deal.etapaNombre,
        diasEnEtapa: 0,
        estancada: false,
      },
      targetEtapaId,
    );
    flashColumn(targetEtapaId);

    try {
      await crmOportunidadService.cambiarEtapa(deal.id, { etapaId: targetEtapaId });
      enqueueSnackbar('Etapa actualizada', { variant: 'success' });
      await loadKanban();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cambiar la etapa'), { variant: 'error' });
      await loadKanban();
    }
  };

  const handleConfirmPerdida = async () => {
    if (!perdidaDeal || motivoPerdidaId === '') {
      enqueueSnackbar('Seleccioná un motivo de pérdida', { variant: 'warning' });
      return;
    }
    setClosingPerdida(true);
    try {
      const etapaActual = findEtapa(perdidaDeal.etapaId) ?? findEtapaAcrossPipelines(perdidaDeal.etapaId);
      const columnaPerdida = columnas.find((c) => c.etapa.esCierrePerdido)?.etapa;
      const etapaPerdida = etapaActual?.esCierrePerdido
        ? perdidaDeal.etapaId
        : resolveEtapaInPipeline(perdidaDeal.pipelineId, columnaPerdida)?.id
          ?? columnaPerdida?.id;
      await crmOportunidadService.cerrarPerdida(perdidaDeal.id, {
        motivoPerdidaId,
        etapaId: etapaPerdida ?? null,
        notaPerdida: notaPerdida.trim() || null,
      });
      const targetId = etapaPerdida ?? perdidaDeal.etapaId;
      insertOrMoveDeal(
        { ...perdidaDeal, etapaId: targetId, estado: 'Perdida', probabilidad: 0 },
        targetId,
      );
      flashColumn(targetId);
      setPerdidaOpen(false);
      setPerdidaDeal(null);
      setNotaPerdida('');
      enqueueSnackbar('Oportunidad marcada como perdida', { variant: 'warning' });
      void loadKpi();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cerrar como perdida'), { variant: 'error' });
      await loadKanban();
    } finally {
      setClosingPerdida(false);
    }
  };

  const resetNuevoContactoForm = () => {
    setNuevoContactoNombre('');
    setNuevoContactoEmpresa('');
    setNuevoContactoEmail('');
    setNuevoContactoTelefono('');
  };

  const openNuevaOportunidad = async (etapaId?: number) => {
    setNuevaEtapaId(etapaId ?? null);
    setNuevaOpen(true);
    setNuevaNombre('');
    setNuevaMonto('');
    setNuevaNroVendedor(nroVendedor);
    setNuevaContactos([]);
    setMostrarAltaContacto(false);
    resetNuevoContactoForm();
    try {
      setLoadingContactos(true);
      const page = await crmContactoService.getAll({
        pageNumber: 1,
        pageSize: 100,
        activo: true,
        nroVendedor: nroVendedor ?? undefined,
      });
      const items = page.items ?? [];
      setContactosOpts(items);
      if (items.length === 0) {
        setMostrarAltaContacto(true);
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar contactos'), { variant: 'error' });
      setContactosOpts([]);
      setMostrarAltaContacto(true);
    } finally {
      setLoadingContactos(false);
    }
  };

  const handleCrearContactoRapido = async () => {
    if (!nuevoContactoNombre.trim()) {
      enqueueSnackbar('Indicá el nombre del contacto', { variant: 'warning' });
      return;
    }
    if (nuevaNroVendedor == null) {
      enqueueSnackbar('Seleccioná el vendedor responsable', { variant: 'warning' });
      return;
    }
    setSavingContacto(true);
    try {
      const created = await crmContactoService.create({
        nombre: nuevoContactoNombre.trim(),
        empresa: nuevoContactoEmpresa.trim() || null,
        email: nuevoContactoEmail.trim() || null,
        telefono: nuevoContactoTelefono.trim() || null,
        nroVendedor: nuevaNroVendedor,
      });
      const asListItem: CrmContactoListItem = {
        id: created.id,
        nombre: created.nombre,
        empresa: created.empresa,
        cargo: created.cargo,
        telefono: created.telefono,
        email: created.email,
        nroVendedor: created.nroVendedor,
        nombreVendedor: created.nombreVendedor,
        nroCliente: created.nroCliente,
        nombreCliente: created.nombreCliente,
        origenId: created.origenId,
        origenNombre: created.origenNombre,
        activo: created.activo,
        fechaAlta: created.fechaAlta,
        tags: created.tags ?? [],
      };
      setContactosOpts((prev) => [asListItem, ...prev.filter((c) => c.id !== asListItem.id)]);
      setNuevaContactos((prev) => [...prev.filter((c) => c.id !== asListItem.id), asListItem]);
      resetNuevoContactoForm();
      setMostrarAltaContacto(false);
      enqueueSnackbar('Contacto creado y asociado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear el contacto'), { variant: 'error' });
    } finally {
      setSavingContacto(false);
    }
  };

  const handleCreateOportunidad = async () => {
    if (typeof pipelineId !== 'number' || !nuevaNombre.trim()) {
      enqueueSnackbar('Indicá un nombre para la oportunidad', { variant: 'warning' });
      return;
    }
    if (nuevaNroVendedor == null) {
      enqueueSnackbar('Seleccioná el vendedor responsable', { variant: 'warning' });
      return;
    }
    if (nuevaContactos.length === 0) {
      enqueueSnackbar('Seleccioná o creá al menos un contacto', { variant: 'warning' });
      setMostrarAltaContacto(true);
      return;
    }
    setSavingNueva(true);
    try {
      const created = await crmOportunidadService.create({
        nombre: nuevaNombre.trim(),
        pipelineId,
        etapaId: nuevaEtapaId,
        nroVendedor: nuevaNroVendedor,
        montoEstimado: nuevaMonto ? Number(nuevaMonto) : null,
        usarSumaCotizaciones: !nuevaMonto,
        contactoIds: nuevaContactos.map((c) => c.id),
        contactoPrincipalId: nuevaContactos[0]?.id ?? null,
      });
      enqueueSnackbar('Oportunidad creada', { variant: 'success' });
      setNuevaOpen(false);
      setNuevaEtapaId(null);
      setNuevaNombre('');
      setNuevaMonto('');
      setNuevaNroVendedor(null);
      setNuevaContactos([]);
      setMostrarAltaContacto(false);
      resetNuevoContactoForm();

      const deal: CrmOportunidadListItem = {
        id: created.id,
        nombre: created.nombre,
        pipelineId: created.pipelineId,
        etapaId: created.etapaId,
        etapaNombre: created.etapaNombre,
        etapaColor: created.etapaColor,
        nroVendedor: created.nroVendedor,
        nombreVendedor: created.nombreVendedor,
        monto: created.monto,
        probabilidad: created.probabilidad,
        estado: created.estado,
        contactoPrincipal: created.contactoPrincipal,
        diasEnEtapa: 0,
        estancada: false,
        fechaAlta: created.fechaAlta,
      };
      insertOrMoveDeal(deal, created.etapaId);
      markHighlighted(created.id, 'humano');
      seenCreateIds.current.add(created.id);
      window.setTimeout(() => seenCreateIds.current.delete(created.id), 15000);
      setSessionNews((n) => n + 1);
      flashColumn(created.etapaId);
      void loadKpi();
      if (vista === 'lista') setListRefreshKey((k) => k + 1);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear oportunidad'), { variant: 'error' });
    } finally {
      setSavingNueva(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Pipeline
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={vista}
                  onChange={(_, v: PipelineVista | null) => {
                    if (v) setVista(v);
                  }}
                >
                  <ToggleButton value="kanban" aria-label="Vista kanban" sx={{ px: 1.25 }}>
                    <ViewKanbanIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="lista" aria-label="Vista lista" sx={{ px: 1.25 }}>
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    void loadKpi();
                    if (vista === 'kanban') void loadKanban();
                    else setListRefreshKey((k) => k + 1);
                  }}
                  disabled={loading}
                >
                  Actualizar
                </Button>
                <Can permiso="crm.oportunidades.nuevo">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openNuevaOportunidad()}
                    disabled={typeof pipelineId !== 'number'}
                    title={isAllPipelines ? 'Seleccioná un pipeline para crear una oportunidad' : undefined}
                  >
                    Nueva oportunidad
                  </Button>
                </Can>
                <Can permiso="crm.pipeline">
                  <IconButton
                    color="primary"
                    aria-label="Configurar pipeline"
                    title={isAllPipelines ? 'Seleccioná un pipeline para configurarlo' : 'Configurar pipeline'}
                    disabled={typeof pipelineId !== 'number'}
                    onClick={() =>
                      navigate(
                        `${ROUTES.crm.config}?editPipelineId=${encodeURIComponent(String(pipelineId))}`,
                      )
                    }
                  >
                    <SettingsIcon />
                  </IconButton>
                </Can>
              </Stack>
            </Stack>

            <CrmPipelineHud
              kpi={kpi}
              connectionState={connectionState}
              sessionNews={sessionNews}
              sessionWins={sessionWins}
            />

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {pipelines.length > 0 && (
                  <Chip
                    label="Todos"
                    clickable
                    color={isAllPipelines ? 'primary' : 'default'}
                    variant={isAllPipelines ? 'filled' : 'outlined'}
                    onClick={() => setPipelineId('all')}
                    sx={{ fontWeight: isAllPipelines ? 600 : 400 }}
                  />
                )}
                {pipelines.map((p) => {
                  const selected = pipelineId === p.id;
                  return (
                    <Chip
                      key={p.id}
                      label={p.nombre}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => setPipelineId(p.id)}
                      sx={{ fontWeight: selected ? 600 : 400 }}
                    />
                  );
                })}
                {pipelines.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No hay pipelines activos
                  </Typography>
                )}
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                  size="small"
                  label="Buscar"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <VendedorSelect
                  value={nroVendedor}
                  onChange={setNroVendedor}
                  allowEmpty
                  emptyOptionLabel="Todos los vendedores"
                  size="small"
                  fullWidth={false}
                  sx={{ width: { xs: '100%', md: 220 }, minWidth: { md: 220 }, flexShrink: 0 }}
                />
              </Stack>
            </Stack>

            {vista === 'lista' ? (
              <CrmPipelineListView
                pipelines={pipelines}
                pipelineId={pipelineId}
                nroVendedor={nroVendedor}
                searchTerm={searchTerm}
                onRowClick={(deal) => setDrawerDeal(deal)}
                refreshKey={listRefreshKey}
              />
            ) : (
              <>
                {loading && !kanban && (
                  <Typography variant="body2" color="text.secondary">
                    Cargando kanban...
                  </Typography>
                )}

                <Box sx={{ overflowX: 'auto', pb: 1 }}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      {columnas.map((col) => (
                        <CrmKanbanColumn
                          key={isAllPipelines ? normEtapaNombre(col.etapa.nombre) || col.etapa.id : col.etapa.id}
                          columna={col}
                          onDealClick={(deal) => setDrawerDeal(deal)}
                          onAddClick={
                            isAllPipelines ? undefined : (etapaId) => openNuevaOportunidad(etapaId)
                          }
                          showPipelineLabel={isAllPipelines}
                          pipelineNombreById={
                            isAllPipelines
                              ? Object.fromEntries(pipelines.map((p) => [p.id, p.nombre]))
                              : undefined
                          }
                          highlightedDealIds={highlightedDealIds}
                          dealFuentes={dealFuentes}
                          flash={
                            flashEtapaId != null && columnMatchesEtapa(col.etapa.id, flashEtapaId)
                          }
                        />
                      ))}
                      {!loading && columnas.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No hay columnas para mostrar. Configurá un pipeline en CRM → Configuración.
                        </Typography>
                      )}
                    </Stack>
                    <DragOverlay>
                      {activeDeal ? (
                        <CrmDealCard
                          deal={activeDeal}
                          dragDisabled
                          pipelineLabel={
                            isAllPipelines
                              ? pipelines.find((p) => p.id === activeDeal.pipelineId)?.nombre ?? null
                              : null
                          }
                        />
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Drawer
        anchor="right"
        open={Boolean(drawerDeal)}
        onClose={() => setDrawerDeal(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            top: { xs: 56, sm: 64 },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
            p: 2.5,
            boxSizing: 'border-box',
            overflowY: 'auto',
          },
        }}
      >
        {drawerDeal && (
          <Stack spacing={2}>
            <Box>
              {highlightedDealIds.has(drawerDeal.id) && (
                <Chip size="small" label="NUEVA" color="primary" sx={{ mb: 1, fontWeight: 700 }} />
              )}
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: '#1976d2',
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                }}
              >
                {drawerDeal.nombre?.trim() ||
                  drawerDeal.contactoPrincipal ||
                  `Oportunidad #${drawerDeal.id}`}
              </Typography>
              <Typography variant="h5" color="text.primary" sx={{ fontWeight: 700, mt: 0.75 }}>
                {formatCurrency(drawerDeal.monto)}
              </Typography>
              {(drawerDeal.cantidadAdjuntos ?? 0) > 0 && (
                <Chip
                  size="small"
                  icon={<AttachFileIcon sx={{ fontSize: '16px !important' }} />}
                  label={`${drawerDeal.cantidadAdjuntos} adjunto${drawerDeal.cantidadAdjuntos === 1 ? '' : 's'}`}
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 1, fontWeight: 600 }}
                />
              )}
            </Box>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Etapa:</strong> {drawerDeal.etapaNombre || '—'}
              </Typography>
              {isAllPipelines && (
                <Typography variant="body2">
                  <strong>Pipeline:</strong>{' '}
                  {pipelines.find((p) => p.id === drawerDeal.pipelineId)?.nombre || '—'}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Contacto:</strong> {drawerDeal.contactoPrincipal || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Vendedor:</strong> {drawerDeal.nombreVendedor || drawerDeal.nroVendedor}
              </Typography>
              <Typography variant="body2">
                <strong>Días en etapa:</strong> {drawerDeal.diasEnEtapa}
                {drawerDeal.estancada ? ' · Estancada' : ''}
              </Typography>
              <Typography variant="body2">
                <strong>Estado:</strong> {drawerDeal.estado}
              </Typography>
              {drawerDeal.probabilidad != null && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Probabilidad:</strong> {drawerDeal.probabilidad}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, Number(drawerDeal.probabilidad)))}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
              )}
            </Stack>

            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
              <CrmAdjuntosPanel
                compact
                oportunidadId={drawerDeal.id}
                onCountChange={(count) => {
                  setDrawerDeal((prev) =>
                    prev && prev.cantidadAdjuntos !== count
                      ? { ...prev, cantidadAdjuntos: count }
                      : prev,
                  );
                  setKanban((prev) => {
                    if (!prev) return prev;
                    let changed = false;
                    const columnas = prev.columnas.map((col) => ({
                      ...col,
                      oportunidades: col.oportunidades.map((o) => {
                        if (o.id !== drawerDeal.id || o.cantidadAdjuntos === count) return o;
                        changed = true;
                        return { ...o, cantidadAdjuntos: count };
                      }),
                    }));
                    return changed ? { ...prev, columnas } : prev;
                  });
                }}
              />
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(ROUTES.crm.oportunidades.detalle(drawerDeal.id))}
            >
              Ver detalle
            </Button>
          </Stack>
        )}
      </Drawer>

      <Dialog
        open={nuevaOpen}
        onClose={() => {
          setNuevaOpen(false);
          setNuevaEtapaId(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Nueva oportunidad
          {nuevaEtapaId != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
              Etapa inicial:{' '}
              {columnas.find((c) => c.etapa.id === nuevaEtapaId)?.etapa.nombre ?? 'seleccionada'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              required
              fullWidth
              value={nuevaNombre}
              onChange={(e) => setNuevaNombre(e.target.value)}
            />
            <VendedorSelect
              value={nuevaNroVendedor}
              onChange={setNuevaNroVendedor}
              label="Vendedor responsable"
              required
              fullWidth
              size="small"
              helperText="Obligatorio para crear el contacto y la oportunidad"
            />
            <Stack spacing={1}>
              <Autocomplete
                multiple
                options={contactosOpts}
                loading={loadingContactos}
                value={nuevaContactos}
                onChange={(_, value) => setNuevaContactos(value)}
                getOptionLabel={(o) =>
                  o.empresa ? `${o.nombre} (${o.empresa})` : o.nombre
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText={
                  loadingContactos
                    ? 'Cargando...'
                    : 'No hay contactos. Creá uno abajo.'
                }
                renderInput={(params) => (
                  <TextField {...params} label="Contactos" required helperText="Al menos uno" />
                )}
              />
              <Box>
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setMostrarAltaContacto((v) => !v)}
                >
                  {mostrarAltaContacto ? 'Ocultar alta de contacto' : 'Nuevo contacto'}
                </Button>
              </Box>
              {mostrarAltaContacto && (
                <Stack
                  spacing={1.5}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Alta rápida de contacto
                  </Typography>
                  <TextField
                    label="Nombre del contacto"
                    required
                    fullWidth
                    size="small"
                    value={nuevoContactoNombre}
                    onChange={(e) => setNuevoContactoNombre(e.target.value)}
                  />
                  <TextField
                    label="Empresa"
                    fullWidth
                    size="small"
                    value={nuevoContactoEmpresa}
                    onChange={(e) => setNuevoContactoEmpresa(e.target.value)}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="Email"
                      fullWidth
                      size="small"
                      value={nuevoContactoEmail}
                      onChange={(e) => setNuevoContactoEmail(e.target.value)}
                    />
                    <TextField
                      label="Teléfono"
                      fullWidth
                      size="small"
                      value={nuevoContactoTelefono}
                      onChange={(e) => setNuevoContactoTelefono(e.target.value)}
                    />
                  </Stack>
                  <Box>
                    <Can permiso="crm.contactos.nuevo">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={handleCrearContactoRapido}
                        disabled={
                          savingContacto ||
                          !nuevoContactoNombre.trim() ||
                          nuevaNroVendedor == null
                        }
                      >
                        {savingContacto ? 'Guardando...' : 'Crear y asociar contacto'}
                      </Button>
                    </Can>
                  </Box>
                </Stack>
              )}
            </Stack>
            <TextField
              label="Monto estimado"
              type="number"
              fullWidth
              value={nuevaMonto}
              onChange={(e) => setNuevaMonto(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevaOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateOportunidad} disabled={savingNueva}>
            {savingNueva ? 'Creando...' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={perdidaOpen}
        onClose={() => {
          setPerdidaOpen(false);
          setNotaPerdida('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cerrar como perdida</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {perdidaDeal?.nombre}
            </Typography>
            <TextField
              select
              fullWidth
              label="Motivo de pérdida"
              required
              value={motivoPerdidaId}
              onChange={(e) => setMotivoPerdidaId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {motivos.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nombre}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Nota / explicación"
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              value={notaPerdida}
              onChange={(e) => setNotaPerdida(e.target.value.slice(0, 2000))}
              helperText={`${notaPerdida.length}/2000 · Opcional`}
              placeholder="Ampliá el motivo con el contexto que necesites…"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPerdidaOpen(false);
              setNotaPerdida('');
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmPerdida}
            disabled={closingPerdida || motivoPerdidaId === ''}
          >
            {closingPerdida ? 'Cerrando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <CrmWinCelebration
        open={Boolean(celebration)}
        nombre={celebration?.nombre ?? ''}
        monto={celebration?.monto ?? 0}
        onDone={() => setCelebration(null)}
      />
    </Box>
  );
};

export default CrmPipelineKanban;
